import { createContext, useMemo } from 'react';
import axios, { AxiosInstance } from 'axios';

import { useUser, withUser } from '@/lib/firebase-auth';
import { useUserLocale } from '@/i18n';

// The default value is not used, but it's meant as a last resort fallback
export const AxiosContext = createContext<AxiosInstance>(axios.create());

interface Props {
  children: React.ReactNode;
}

export function AxiosProvider({ children }: Props) {
  const user = useUser();
  const userLocale = useUserLocale();

  const memoizedAxiosInstance = useMemo(() => {
    const axiosInstance = axios.create({
      baseURL: process.env.NEXT_PUBLIC_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-User-Language': userLocale,
      },
    });

    // Inject the user's token into the request headers if available
    axiosInstance.interceptors.request.use(async (config) => {
      if (user) {
        const token = await user.getIdToken();
        if (token) {
          config.headers.Authorization = token;
        }
      }

      return config;
    });
    return axiosInstance;
  }, [user, userLocale]);
  return <AxiosContext.Provider value={memoizedAxiosInstance}>{children}</AxiosContext.Provider>;
}

export default withUser<Props>()(AxiosProvider);
